import { Globals } from '../../modules/Globals';
import axios from 'axios';
import { IRestaurant } from '../../interfaces/IRestaurant';
import { ICategory } from '../../interfaces/ICategory';
import { IMember } from '../../interfaces/IMember';
import { TaxDisplayMode } from '../../enums/TaxDisplayMode';
import { RoundMode } from '../../enums/RoundMode';
import { IExpiringRestaurant } from '../../interfaces/IExpiringRestaurant';
import { IActiveLocation } from '../../interfaces/IActiveLocation';
import { IPaymentData } from '../../interfaces/IPaymentData';
import { IPaginated } from '../../interfaces/IPaginated';
import type {
  ICreateRestaurantFlowFields,
  TSetupStage,
} from '@/interfaces/IStoreSetup';
import type {
  IUpdateDesignBody,
  IUpdateDesignResponse,
} from '@/interfaces/IStorefrontStyle';

const apiUrl = Globals.apiPath + '/restaurants';

export interface IRestaurantSettings {
  showDiscounts: boolean;
  variantsFullPrice: boolean;
  showDecimals: boolean;
  hideSymbol: boolean;
  taFromSeat: boolean;
  multipleMenus: boolean;
  taxDelivery: boolean;
  taxMode: TaxDisplayMode;
  menu1Id?: number;
  menu2Id?: number;
  menu3Id?: number;
  roundMode?: RoundMode;
  roundInterval?: number;
  showDisabledItems: boolean;
  /** Owner switch «Lo más pedido» (CON-5). */
  showPopular: boolean;
  paymentData?: IPaymentData;
  autoRenew?: boolean;
}

/** The legacy body plus the optional flow fields (`setupSource: 'flow'`, plan §6.2). */
export interface ICreateRestaurantRequest extends ICreateRestaurantFlowFields {
  stringId: string;
  name: string;
  currency: string;
  country: string;
  lat: number;
  lon: number;
  address: string | null;
  province: string | null;
  city: string | null;
  locationId: number | null;
  userWhatsapp: string | null;
}

/** `POST /restaurants` answer: `setup` is present only for flow creations. */
export interface ICreatedRestaurant extends IRestaurant {
  setup?: { stage: TSetupStage };
}

export default class RestaurantsService {
  constructor() {}

  async getAll(): Promise<IRestaurant[]> {
    let res = await axios.get<IRestaurant[]>(apiUrl);
    return res.data;
  }

  async getById(id: number): Promise<IRestaurant> {
    let res = await axios.get<IRestaurant>(`${apiUrl}/${id}`);
    return res.data;
  }

  static async getFullRestaurant(id: number): Promise<IRestaurant> {
    let res = await axios.get<IRestaurant>(`${apiUrl}/${id}/full`);
    return res.data;
  }

  static async getFullRestaurantBySlug(slug: string): Promise<IRestaurant> {
    let res = await axios.get<IRestaurant>(`${apiUrl}/s/${slug}/full`);
    return res.data;
  }

  async isIdFree(id: string): Promise<boolean> {
    let res = await axios.get(`${apiUrl}/isIdFree?id=${id}`);
    return res.data.free;
  }

  async getSettings(id: number): Promise<IRestaurantSettings> {
    let res = await axios.get<IRestaurantSettings>(`${apiUrl}/${id}/settings`);
    return res.data;
  }

  async getExpiringRestaurants(pa: boolean = false) {
    let res = await axios.get<IExpiringRestaurant[]>(
      `${apiUrl}/expiring` + (pa ? '?pa=1' : '')
    );
    return res.data;
  }

  async getResumedRestaurants(data: {
    page?: number;
    itemsPerPage?: number;
    search?: string | null;
    order?: string | null;
    hideLite?: boolean;
    onlyPaid?: boolean;
    onlyHaveOrders?: boolean;
    pa?: boolean;
  }) {
    let res = await axios.get<IPaginated<IExpiringRestaurant>>(
      `${apiUrl}/resumed?page=${data.page ?? 1}` +
        `&itemsPerPage=${data.itemsPerPage ?? 5}` +
        (data.search ? `&search=${data.search}` : '') +
        (data.order ? `&order=${data.order}` : '') +
        (data.hideLite ? `&hideLite=1` : '') +
        (data.onlyPaid ? `&onlyPaid=1` : '') +
        (data.onlyHaveOrders ? `&onlyHaveOrders=1` : '') +
        (data.pa ? `&pa=1` : '')
    );
    return res.data;
  }

  async addPlan(
    id: number,
    tier: number,
    months: number
  ): Promise<IRestaurant> {
    let res = await axios.put<IRestaurant>(`${apiUrl}/${id}/addPlan`, {
      tier,
      months,
    });
    return res.data;
  }

  async getActiveLocations(pa: boolean = false) {
    let res = await axios.get<IActiveLocation[]>(
      `${apiUrl}/activeLocations` + (pa ? '?pa=1' : '')
    );
    return res.data;
  }

  static async getMembers(id: number) {
    let res = await axios.get<IMember[]>(`${apiUrl}/${id}/members`);
    return res.data;
  }

  async getMember(id: number, userId: number) {
    let res = await axios.get<IMember>(`${apiUrl}/${id}/members/${userId}`);
    return res.data;
  }

  async saveMember(id: number, member: IMember) {
    let res = await axios.put<IMember>(`${apiUrl}/${id}/members`, member);
    return res.data;
  }

  async deleteMember(id: number, userId: number) {
    let res = await axios.delete<IMember>(`${apiUrl}/${id}/members/${userId}`);
    return res.data;
  }

  async saveSettings(
    id: number,
    settings: IRestaurantSettings | any
  ): Promise<IRestaurantSettings> {
    let res = await axios.put<IRestaurantSettings>(
      `${apiUrl}/${id}/settings`,
      settings
    );
    return res.data;
  }

  async getByStringId(id: string): Promise<IRestaurant> {
    let res = await axios.get<IRestaurant>(`${apiUrl}/s/${id}`);
    return res.data;
  }

  async getHomeStats(
    id: number
  ): Promise<{ itemCount: number; categoryCount: number }> {
    let res = await axios.get(`${apiUrl}/${id}/homeStats`);
    return res.data;
  }

  /** Flow creations (and their idempotent replays) also return `setup.stage`. */
  async create(
    restaurant: ICreateRestaurantRequest
  ): Promise<ICreatedRestaurant> {
    let res = await axios.post<ICreatedRestaurant>(apiUrl, restaurant);
    return res.data;
  }

  async update(restaurant: IRestaurant): Promise<IRestaurant> {
    let res = await axios.put<IRestaurant>(
      `${apiUrl}/${restaurant.id}`,
      restaurant
    );
    return res.data;
  }

  async delete(restaurant: IRestaurant): Promise<IRestaurant> {
    let res = await axios.put<IRestaurant>(`${apiUrl}/${restaurant.id}`);
    return res.data;
  }

  static async getCategories(
    id: number,
    menuId: number = null
  ): Promise<ICategory[]> {
    let res = await axios.get<ICategory[]>(
      `${apiUrl}/${id}/categories` + (menuId ? `?m=${menuId}` : '')
    );
    return res.data;
  }

  /** «Diseño y estilo»: saves the style spec and the banner focal point (CON-4). */
  static async saveDesign(
    id: number,
    body: IUpdateDesignBody
  ): Promise<IUpdateDesignResponse> {
    const res = await axios.put<IUpdateDesignResponse>(
      `${apiUrl}/${id}/design`,
      body
    );
    return res.data;
  }
}
